<template>
  <transition
    css
    enter-active-class="collapsing"
    enter-to-class="collapse show"
    leave-class="collapse show"
    leave-active-class="collapsing"
    leave-to-class="collapse"
    :appear="appear"
    @after-leave="onAfterLeave"
    @enter="onEnter"
    @after-enter="onAfterEnter"
    @leave="onLeave"
  >
    <component
      :is="tag"
      v-show="show"
      :id="_id"
      :class="classObject"
      @click="clickHandler"
    >
      <slot />
    </component>
  </transition>
</template>

<script>

import { useListenOnRoot } from './useListenOnRoot'

const MODEL_EVENT_NAME = 'update:modelValue'
const ROOT_EVENT_NAME_ACCORDION = 'bv:collapse:accordion'
const ROOT_ACTION_EVENT_NAME_TOGGLE = 'bv::toggle::collapse'
const ROOT_ACTION_EVENT_NAME_REQUEST_STATE = 'bv::request-state::collapse'
const ROOT_EVENT_NAME_STATE = 'bv::collapse::state'
const ROOT_EVENT_NAME_SYNC_STATE = 'bv::collapse::sync-state'

// Requesting an elements offsetHight will trigger a reflow of the element content
const reflow = (el) => el.offsetHeight

export default {
  name: 'BCollapse',
  props: {
    tag: {
      type: String,
      default: 'div'
    },
    visible: Boolean,
    id: {
      type: String,
      default: null
    },
    accordion: {
      type: String,
      default: ''
    },
    appear: Boolean,
    modelValue: Boolean
  },
  emits: ['show', 'shown', 'hide', 'hidden', MODEL_EVENT_NAME],
  setup() {
    const { listenOnRoot, emitOnRoot } = useListenOnRoot()
    return {
      listenOnRoot,
      emitOnRoot
    }
  },
  data() {
    return {
      show: this.visible,
      transitioning: false
    }
  },
  computed: {
    classObject() {
      const { transitioning } = this

      return {
        collapse: !transitioning,
        show: this.show && !transitioning
      }
    }
  },
  watch: {
    visible(newValue) {
      if (newValue !== this.show) {
        this.show = newValue
      }
    },
    show(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.emitState()
      }
    }
  },
  created() {
    this.show = this.visible
    this._id = this.id ?? `_BV_toggle_${parseInt(Math.random() * 200)}`
  },
  mounted() {
    this.show = this.visible
    // Listen for toggle events to open/close us
    this.listenOnRoot(ROOT_ACTION_EVENT_NAME_TOGGLE, this.handleToggleEvent)
    // Listen to other collapses for accordion events
    this.listenOnRoot(ROOT_EVENT_NAME_ACCORDION, this.handleAccordionEvent)
    this.$nextTick(() => {
      this.emitState()
    })
    // Listen for "Sync state" requests from `v-b-toggle`
    this.listenOnRoot(ROOT_ACTION_EVENT_NAME_REQUEST_STATE, id => {
      if (id === this._id) {
        this.$nextTick(this.emitSync)
      }
    })
  },
  methods: {
    toggle() {
      this.show = !this.show
    },
    onEnter(el) {
      el.style.height = 0
      requestAnimationFrame(() => {
        reflow(el)
        el.style.height = `${el.scrollHeight}px`
      })

      this.transitioning = true
      this.$emit('show')
    },
    onAfterEnter(el) {
      el.style.height = ''
      this.transitioning = false
      this.$emit('shown')
    },
    onLeave(el) {
      el.style.height = 'auto'
      el.style.display = 'block'
      el.style.height = `${el.getBoundingClientRect().height}px`
      reflow(el)
      el.style.height = 0
      this.transitioning = true
      this.$emit('hide')
    },
    onAfterLeave(el) {
      el.style.height = ''
      this.transitioning = false
      this.$emit('hidden')
    },
    emitState() {
      const { show, accordion } = this
      const id = this._id

      this.$emit(MODEL_EVENT_NAME, show)

      // Let `v-b-toggle` know the state of this collapse
      this.emitOnRoot(ROOT_EVENT_NAME_STATE, { id, show })
      if (accordion && show) {
        // Tell the other collapses in this accordion to close
        this.emitOnRoot(ROOT_EVENT_NAME_ACCORDION, { id, accordion })
      }
    },
    emitSync() {
      // Emit a private event every time this component updates to ensure
      // the toggle button is in sync with the collapse's state
      // It is emitted regardless if the visible state changes
      this.emitOnRoot(ROOT_EVENT_NAME_SYNC_STATE, { _id: this._id, show: this.show })
    },
    clickHandler(evt) {
      const { target } = evt
      if (!target || window.getComputedStyle(this.$el).display !== 'block') {
        return
      }
      if (target.matches('.nav-link,.dropdown-item') || target.closest('.nav-link,.dropdown-item')) {
        this.show = false
      }
    },
    handleToggleEvent(e) {
      if (e.detail === this._id) {
        this.toggle()
      }
    },
    handleAccordionEvent(e) {
      const { accordion, show } = this
      if (!accordion || accordion !== e.detail.accordion) {
        return
      }
      const isThis = e.detail.id === this._id
      // Open this collapse if not shown or
      // close this collapse if shown
      if ((isThis && !show) || (!isThis && show)) {
        this.toggle()
      }
    }
  }
}
</script>
